<template>
  <div class="call-result-type-select-container">
    <span
      v-if="isDisplayOnly"
    >
      {{ selectedCallResultType(model)?.name }}
    </span>
    <BSelect
      v-else
      v-model="model"
      v-model:valid="isValid"
      :items="items"
      :required="required"
      item-text="name"
      item-value="value"
      fit
      filterable
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import { CustomCallResultTypeSummary, DefaultCallResultTypeSummary } from '@/api/openapi';
import { CallResultTypeApiService } from '@/api/user/resources/call_result_type';
import { useWait } from '@/composable/vue-wait';

type TProps = {
  required: boolean;
  isDisplayOnly: boolean;
  waitKeySuffix?: string;
};

type TEmits = {
  'change': [string | null];
};

const props = defineProps<TProps>();
const emit = defineEmits<TEmits>();

const { doActionWithWait } = useWait();

type TModelValueType = 'custom' | 'default';

const CUSTOM_PREFIX = 'custom_';
const DEFAULT_PREFIX = 'default_';

const model = defineModel<string | null>();
const isValid = defineModel<boolean>('isValid', { default: false });

const customCallResultTypes = ref<CustomCallResultTypeSummary[]>([]);
const defaultCallResultTypes = ref<DefaultCallResultTypeSummary[]>([]);

const items = computed(() => {
  return [
    ...defaultCallResultTypes.value.map((defaultCallResultType) => ({
      value: buildModelValue(defaultCallResultType.id, 'default'),
      name: defaultCallResultType.name,
    })),
    ...customCallResultTypes.value.map((customCallResultType) => ({
      value: buildModelValue(customCallResultType.id, 'custom'),
      name: customCallResultType.name,
    })),
  ];
});

const buildModelValue = (value: number, type: TModelValueType) => {
  if (value == null) return null;
  if (type === 'custom') {
    return `${CUSTOM_PREFIX}${value}`;
  } else if (type === 'default') {
    return `${DEFAULT_PREFIX}${value}`;
  }
  return null;
};

const loadCallResultTypes = async () => {
  const api = new CallResultTypeApiService();
  await doActionWithWait(`fetchCallResultTypes${props.waitKeySuffix ?? ''}`, async () => {
    const { data } = await api.getCallResultTypeSummaries();
    customCallResultTypes.value = data.customCallResultTypes;
    defaultCallResultTypes.value = data.defaultCallResultTypes;
  });
};

loadCallResultTypes();

const selectedCallResultType = (callResultTypeId: string | null | undefined) => {
  return items.value.find((callResultType) => callResultType.value === callResultTypeId);
};

const handleChange = () => {
  emit('change', model.value);
};

watch(model, () => {
  handleChange();
});
</script>

<style lang="scss" scoped>
.call-result-type-select-container {
  position: relative;
}
</style>
